import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaHeartbeat, FaFileAlt, FaUser, FaUpload, FaTrash, FaDownload, FaCheckCircle, FaLock, FaPlus, FaTimes,
  FaImage, FaAppleAlt, FaVideo,
} from 'react-icons/fa';
import { DIET_SECTIONS, emptyDietForm } from '../../Files/dietForm';
import VitalsDynamicForm from '../../Components/CommonComponents/VitalsDynamicForm';
import CameraCapture from '../../Components/CommonComponents/CameraCapture';
import {
  fetchPortalView,
  savePortalProfile,
  submitPortalVitals,
  updatePortalVitals,
  uploadPortalFiles,
  deletePortalFile,
  portalFileUrl,
  submitPortalDietForm,
} from '../../Services/patientProfile';

const FIELD_LABELS = {
  email: 'Email',
  bloodGroup: 'Blood group',
  maritalStatus: 'Marital status',
  motherTongue: 'Mother tongue',
  religion: 'Religion',
  education: 'Education',
  country: 'Country',
  state: 'State',
  city: 'City',
  location: 'Address',
  pincode: 'PIN code',
  profession: 'Profession',
  dateOfBirth: 'Date of birth',
  anniversary: 'Anniversary',
  weight: 'Weight (kg)',
  height: 'Height (cm)',
};

const FIELD_TYPES = {
  email: 'email',
  dateOfBirth: 'date',
  anniversary: 'date',
  weight: 'number',
  height: 'number',
  pincode: 'number',
};

const fmtDateTime = (value) =>
  new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const nowLocalInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const Shell = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      {children}
    </div>
  </div>
);

const inputCls =
  'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500';

/* ------------------------------- my details ------------------------------- */

const DetailsSection = ({ token, view, onSaved }) => {
  const [form, setForm] = useState(() =>
    view.editableFields.reduce((acc, f) => ({ ...acc, [f]: view.patient[f] ?? '' }), {})
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await savePortalProfile(token, form);
      toast.success('Your details have been saved');
      onSaved?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not save your details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-800">My details</h2>
      <p className="mt-0.5 text-sm text-slate-500">Keep these up to date so the clinic can reach you.</p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-slate-500">Name</label>
          <input className={`${inputCls} bg-slate-50`} value={view.patient.name} disabled />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-500">
            Mobile <FaLock className="ml-1 inline h-3 w-3 text-slate-400" />
          </label>
          <input className={`${inputCls} bg-slate-50`} value={view.patient.mobile || ''} disabled />
          <p className="mt-1 text-xs text-slate-500">
            Contact the clinic to change your registered number.
          </p>
        </div>

        {view.editableFields.map((field) => (
          <div key={field}>
            <label className="block text-sm font-medium text-slate-700">{FIELD_LABELS[field] || field}</label>
            <input
              type={FIELD_TYPES[field] || 'text'}
              className={inputCls}
              value={form[field] ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="mt-5 rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
      >
        {saving ? 'Saving…' : 'Save my details'}
      </button>
    </div>
  );
};

/* -------------------------------- my vitals ------------------------------- */

const VitalsSection = ({ token, view, onSaved }) => {
  const [openId, setOpenId] = useState(null);
  const [values, setValues] = useState({});
  const [recordedAt, setRecordedAt] = useState(nowLocalInput());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const byTemplate = useMemo(() => {
    const map = new Map();
    view.records.forEach((r) => {
      const list = map.get(String(r.templateId)) || [];
      list.push(r);
      map.set(String(r.templateId), list);
    });
    return map;
  }, [view.records]);

  const open = (assignment) => {
    setOpenId(assignment._id);
    setValues({});
    setRecordedAt(nowLocalInput());
    setNotes('');
    setEditing(null);
  };

  const submit = async (assignment) => {
    setSaving(true);
    try {
      await submitPortalVitals(token, {
        templateId: assignment.templateId,
        recordedAt: new Date(recordedAt).toISOString(),
        values,
        notes,
      });
      toast.success('Thank you — your reading has been sent to the clinic');
      setOpenId(null);
      onSaved?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not save your reading');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (record) => {
    setSaving(true);
    try {
      await updatePortalVitals(token, record._id, { values, notes });
      toast.success('Reading updated');
      setEditing(null);
      onSaved?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not update your reading');
    } finally {
      setSaving(false);
    }
  };

  if (!view.assignments.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
        <FaHeartbeat className="mx-auto h-8 w-8 text-slate-300" />
        <p className="mt-3 font-medium text-slate-700">Nothing to record right now</p>
        <p className="mt-1 text-sm text-slate-500">
          Your doctor has not asked you to track any readings yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {view.assignments.map((assignment) => {
        const template = assignment.template;
        if (!template) return null;
        const history = byTemplate.get(String(assignment.templateId)) || [];
        const isOpen = openId === assignment._id;

        return (
          <div key={assignment._id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-800">{template.name}</h3>
                <p className="mt-0.5 text-sm text-slate-500">
                  {assignment.frequency}
                  {assignment.instructions ? ` · ${assignment.instructions}` : ''}
                </p>
                {template.description && (
                  <p className="mt-1 text-sm text-slate-600">{template.description}</p>
                )}
              </div>
              <button
                onClick={() => (isOpen ? setOpenId(null) : open(assignment))}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold ${
                  isOpen
                    ? 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                    : 'bg-sky-600 text-white hover:bg-sky-700'
                }`}
              >
                {isOpen ? (
                  <>
                    <FaTimes /> Cancel
                  </>
                ) : (
                  <>
                    <FaPlus /> Add reading
                  </>
                )}
              </button>
            </div>

            {isOpen && (
              <div className="mt-4 rounded-xl bg-sky-50/60 p-4">
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-slate-700">When did you take this?</label>
                  <input
                    type="datetime-local"
                    className={inputCls}
                    value={recordedAt}
                    onChange={(e) => setRecordedAt(e.target.value)}
                  />
                </div>

                <div className="mt-4">
                  <VitalsDynamicForm template={template} values={values} onChange={setValues} />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700">
                    Anything you want the doctor to know? (optional)
                  </label>
                  <textarea rows={2} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>

                <button
                  onClick={() => submit(assignment)}
                  disabled={saving}
                  className="mt-4 w-full rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60 sm:w-auto"
                >
                  {saving ? 'Sending…' : 'Send to my clinic'}
                </button>
              </div>
            )}

            {/* Values only — trends and charts are for the doctor. */}
            {history.length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  What you have recorded
                </h4>
                <ul className="mt-2 space-y-2">
                  {history.slice(0, 10).map((record) => {
                    const isEditing = editing === record._id;

                    return (
                      <li key={record._id} className="rounded-xl border border-slate-200 p-3">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-slate-700">{fmtDateTime(record.recordedAt)}</span>
                          {record.canEdit ? (
                            <button
                              onClick={() => {
                                setEditing(isEditing ? null : record._id);
                                setValues(record.values || {});
                                setNotes(record.notes || '');
                              }}
                              className="text-xs font-medium text-sky-600 hover:underline"
                            >
                              {isEditing ? 'Cancel' : 'Correct this'}
                            </button>
                          ) : (
                            <span className="text-[11px] text-slate-400">Recorded at the clinic</span>
                          )}
                        </div>

                        {isEditing ? (
                          <div className="mt-3">
                            <VitalsDynamicForm template={template} values={values} onChange={setValues} />
                            <button
                              onClick={() => saveEdit(record)}
                              disabled={saving}
                              className="mt-3 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
                            >
                              {saving ? 'Saving…' : 'Save correction'}
                            </button>
                          </div>
                        ) : (
                          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                            {(template.fields || []).map((f) => {
                              const v = record.values?.[f.key];
                              if (v === undefined || v === null || v === '') return null;
                              return (
                                <span key={f.key}>
                                  {f.label}:{' '}
                                  <strong className="text-slate-900">
                                    {v === true ? 'Yes' : v === false ? 'No' : v}
                                  </strong>
                                  {f.unit ? ` ${f.unit}` : ''}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {record.notes && !isEditing && (
                          <p className="mt-1 text-xs italic text-slate-500">“{record.notes}”</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ------------------------------ my documents ------------------------------ */

// Pictures and documents are separate permissions on the link, so the same
// section renders either one: it uploads into its own category and lists only
// its own files. The server refuses the other category regardless.
const FILE_KINDS = {
  pictures: {
    category: 'picture',
    title: 'Upload pictures',
    detail: 'Photographs your doctor has asked for — for example of a skin condition or a wound.',
    listTitle: 'My pictures',
    accept: 'image/*',
    camera: true,
    icon: FaImage,
  },
  documents: {
    category: 'document',
    title: 'Upload documents',
    detail: 'Lab reports, scans, prescriptions — anything your doctor has asked you to send.',
    listTitle: 'My documents',
    accept: undefined,
    camera: true,
    icon: FaFileAlt,
  },
};

const isPicture = (doc) => doc.category === 'picture';

const DocumentsSection = ({ token, view, onSaved, kind = 'documents' }) => {
  const config = FILE_KINDS[kind];
  const Icon = config.icon;
  const files = view.documents.filter((doc) => (kind === 'pictures' ? isPicture(doc) : !isPicture(doc)));
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!selected.length) return toast.error('Choose a file first');
    setUploading(true);
    try {
      await uploadPortalFiles(token, selected, { notes, category: config.category });
      toast.success('Uploaded — your clinic can now see these');
      setSelected([]);
      setNotes('');
      onSaved?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (doc) => {
    try {
      await deletePortalFile(token, doc._id);
      toast.success('File removed');
      onSaved?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not remove the file');
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800">{config.title}</h2>
        <p className="mt-0.5 text-sm text-slate-500">{config.detail}</p>

        <input
          type="file"
          multiple
          accept={config.accept}
          onChange={(e) => setSelected(Array.from(e.target.files || []))}
          className="mt-4 block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-sky-50 file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-sky-700 hover:file:bg-sky-100"
        />
        {config.camera && (
          <CameraCapture className="mt-2" onCapture={(file) => setSelected((prev) => [...prev, file])} />
        )}

        {!!selected.length && (
          <div className="mt-3 flex flex-wrap gap-2">
            {selected.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
                {f.name}
                <button onClick={() => setSelected((s) => s.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600">
                  <FaTimes />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="mt-3">
          <label className="block text-sm font-medium text-slate-700">A note for the clinic (optional)</label>
          <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <button
          onClick={submit}
          disabled={uploading || !selected.length}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50 sm:w-auto"
        >
          <FaUpload /> {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-semibold text-slate-800">{config.listTitle}</h3>
        {!files.length ? (
          <p className="mt-3 text-sm text-slate-500">Nothing uploaded yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {files.map((doc) => (
              <li key={doc._id} className="flex flex-wrap items-center gap-3 py-3">
                <Icon className="shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-800">{doc.title || doc.originalname}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(doc.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                    {doc.source === 'clinic' && ' · added by the clinic'}
                  </p>
                </div>
                <a
                  href={portalFileUrl(token, doc._id, true)}
                  className="rounded p-2 text-slate-500 hover:bg-slate-100"
                  title="Download"
                >
                  <FaDownload />
                </a>
                {doc.canDelete && (
                  <button onClick={() => remove(doc)} className="rounded p-2 text-rose-400 hover:bg-rose-50" title="Remove">
                    <FaTrash />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/* -------------------------------- diet form ------------------------------- */

const DietField = ({ field, value, onChange }) => {
  const shared = { id: `diet-${field.name}`, name: field.name, value: value || '', onChange, className: inputCls };
  return (
    <div className={field.full ? 'sm:col-span-2' : ''}>
      <label htmlFor={shared.id} className="block text-sm font-medium text-slate-700">
        {field.label}
        {field.unit && <span className="ml-1 font-normal text-slate-400">({field.unit})</span>}
      </label>
      {field.type === 'select' ? (
        <select {...shared}>
          <option value="">Select an option</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : field.type === 'textarea' ? (
        <textarea {...shared} rows={3} placeholder={field.placeholder || ''} />
      ) : (
        <input {...shared} type={field.type} placeholder={field.placeholder || ''} min={field.min} max={field.max} />
      )}
    </div>
  );
};

const DietSection = ({ token, view, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyDietForm);
  const [saving, setSaving] = useState(false);
  const submitted = view.dietForms || [];

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const answers = Object.entries(form).reduce(
      (acc, [k, v]) => ({ ...acc, [k]: typeof v === 'string' ? v.trim() : v }),
      {}
    );
    if (!Object.values(answers).some((v) => String(v || '') !== '')) {
      return toast.error('Please answer at least a few questions before submitting');
    }
    setSaving(true);
    try {
      const res = await submitPortalDietForm(token, answers);
      toast.success(res?.message || 'Your diet form has been sent to the clinic');
      setForm(emptyDietForm());
      setOpen(false);
      onSaved?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not send your diet form');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-800">Diet &amp; nutrition questionnaire</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Your answers help your doctor plan a diet around how you actually eat. Nothing is compulsory.
            </p>
          </div>
          <button
            onClick={() => setOpen((o) => !o)}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold ${
              open ? 'border border-slate-300 text-slate-700 hover:bg-slate-50' : 'bg-sky-600 text-white hover:bg-sky-700'
            }`}
          >
            {open ? (
              <>
                <FaTimes /> Cancel
              </>
            ) : (
              <>
                <FaPlus /> {submitted.length ? 'Fill in again' : 'Fill in the form'}
              </>
            )}
          </button>
        </div>

        {open && (
          <form onSubmit={submit} className="mt-4 space-y-5">
            {DIET_SECTIONS.map((section) => (
              <fieldset key={section.id} className="rounded-xl bg-sky-50/60 p-4">
                <legend className="px-1 text-sm font-semibold text-slate-800">{section.title}</legend>
                <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {section.fields.map((field) => (
                    <DietField key={field.name} field={field} value={form[field.name]} onChange={onChange} />
                  ))}
                </div>
              </fieldset>
            ))}
            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60 sm:w-auto"
            >
              {saving ? 'Sending…' : 'Send to my clinic'}
            </button>
          </form>
        )}
      </div>

      {submitted.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800">Forms you have sent</h3>
          <ul className="mt-3 divide-y divide-slate-100 text-sm">
            {submitted.map((f) => (
              <li key={f._id} className="flex items-center justify-between py-2">
                <span className="text-slate-700">{fmtDateTime(f.submittedAt)}</span>
                <span className="text-xs text-slate-500">Received</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* --------------------------- video consultations --------------------------- */

const slotLabel = (slot) => {
  if (!slot) return '';
  if (typeof slot === 'string') return slot;
  return slot.label || [slot.start || slot.from, slot.end || slot.to].filter(Boolean).join(' – ');
};

const ConsultationsSection = ({ view }) => {
  const sessions = view.consultations || [];

  if (!sessions.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
        <FaVideo className="mx-auto h-8 w-8 text-slate-300" />
        <p className="mt-3 font-medium text-slate-700">No video consultations yet</p>
        <p className="mt-1 text-sm text-slate-500">When the clinic books one for you, it will appear here.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-800">My video consultations</h2>
      <ul className="mt-3 divide-y divide-slate-100">
        {sessions.map((c) => (
          <li key={c._id} className="flex flex-wrap items-center gap-3 py-3">
            <FaVideo className="shrink-0 text-slate-400" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800">
                {c.bookDate || (c.scheduledAt ? fmtDateTime(c.scheduledAt) : 'Date to be confirmed')}
                {slotLabel(c.bookTimeSlot) ? ` · ${slotLabel(c.bookTimeSlot)}` : ''}
              </p>
              <p className="text-xs text-slate-500">
                {c.doctorName ? `With ${c.doctorName} · ` : ''}
                {c.status}
              </p>
            </div>
            {c.joinPath && (
              <a
                href={c.joinPath}
                className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
              >
                Join
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

/* --------------------------------- page ---------------------------------- */

export default function PatientPortal() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [section, setSection] = useState('vitals');

  const load = useCallback(async () => {
    try {
      setView(await fetchPortalView(token));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'This link is no longer valid');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Shell>
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" />
        <p className="mt-4 text-sm text-slate-500">Opening your records…</p>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <p className="font-medium text-rose-600">{error}</p>
        <p className="mt-2 text-sm text-slate-500">Please contact your clinic for a new link.</p>
      </Shell>
    );
  }

  // Each tab is a permission the clinic sets on this link; the server enforces
  // the same switches, this only hides what would be refused anyway.
  const allSections = [
    { id: 'vitals', on: view.allow?.vitals !== false, label: 'My vitals', icon: <FaHeartbeat /> },
    { id: 'pictures', on: view.allow?.pictures === true, label: 'Pictures', icon: <FaImage /> },
    { id: 'documents', on: view.allow?.documents === true, label: 'Documents', icon: <FaFileAlt /> },
    { id: 'diet', on: view.allow?.dietForm === true, label: 'Diet form', icon: <FaAppleAlt /> },
    { id: 'consultations', on: view.allow?.videoConsultation === true, label: 'Video consultation', icon: <FaVideo /> },
    { id: 'details', on: view.allow?.profile !== false, label: 'My details', icon: <FaUser /> },
  ];
  const sections = allSections.filter((s) => s.on);
  const disabledLabels = allSections
    .filter((s) => !s.on && ['pictures', 'documents', 'diet', 'consultations'].includes(s.id))
    .map((s) => s.label);

  const current = sections.some((s) => s.id === section) ? section : sections[0]?.id;

  if (!sections.length) {
    return (
      <Shell>
        <p className="font-medium text-slate-700">Nothing has been shared on this link yet</p>
        <p className="mt-2 text-sm text-slate-500">Please contact your clinic.</p>
      </Shell>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-16">
      <header className="bg-white shadow-sm">
        <div className="mx-auto max-w-3xl px-4 py-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">My health record</p>
          <h1 className="mt-0.5 text-2xl font-bold text-slate-800">{view.patient.name}</h1>
          {view.patient.patientId && (
            <p className="text-sm text-slate-500">Patient ID: {view.patient.patientId}</p>
          )}
        </div>

        <nav className="mx-auto flex max-w-3xl gap-1 overflow-x-auto px-4">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition ${
                current === s.id
                  ? 'border-sky-600 text-sky-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        {current === 'vitals' && <VitalsSection token={token} view={view} onSaved={load} />}
        {current === 'pictures' && <DocumentsSection key="pictures" kind="pictures" token={token} view={view} onSaved={load} />}
        {current === 'documents' && <DocumentsSection key="documents" kind="documents" token={token} view={view} onSaved={load} />}
        {current === 'diet' && <DietSection token={token} view={view} onSaved={load} />}
        {current === 'consultations' && <ConsultationsSection view={view} />}
        {current === 'details' && <DetailsSection key={view.patient._id} token={token} view={view} onSaved={load} />}

        <p className="flex items-start gap-2 pt-2 text-xs text-slate-500">
          <FaCheckCircle className="mt-0.5 shrink-0 text-emerald-500" />
          Everything you record here goes straight to your clinic. This page is private to you — please do not
          forward the link.
        </p>

        {disabledLabels.length > 0 && (
          <p className="flex items-start gap-2 text-xs text-slate-400">
            <FaLock className="mt-0.5 shrink-0" />
            {disabledLabels.join(', ')} {disabledLabels.length > 1 ? 'are' : 'is'} not enabled by your clinic on this
            link.
          </p>
        )}
      </main>
    </div>
  );
}
